/**
 * API Configuration and Client Factory
 *
 * Manages multiple API endpoints with environment-based URL configuration.
 * All API clients are auto-generated - DO NOT EDIT MANUALLY.
 */

import createClient from "openapi-fetch";
import type { paths as RisPublicApiPaths } from "./generated/ris-public-api.schema";
import type { paths as DeviceServiceApiPaths } from "./generated/device-service-api.schema";
import { apiService } from "../services/apiService";

/**
 * Environment configuration
 */
export type Environment = "dev" | "qa" | "stage" | "beta" | "prod";

interface ApiConfig {
  risPublicApi: string;
  deviceServiceApi: string;
}

const API_URLS: Record<Environment, ApiConfig> = {
  dev: {
    risPublicApi: "https://dev.api.honeywellhome.com/ris-public-api",
    deviceServiceApi: "https://dev.api.honeywellhome.com/ris-public-api",
  },
  qa: {
    risPublicApi: "https://qa.api.honeywellhome.com/ris-public-api",
    deviceServiceApi: "https://qa.api.honeywellhome.com/ris-public-api",
  },
  stage: {
    risPublicApi: "https://api.stg.resideo.com/ris-public-api",
    deviceServiceApi: "https://api.stg.resideo.com/ris-public-api",
  },
  beta: {
    risPublicApi: "https://b1prod-api.fortiq.titans.cloud/ris-public-api",
    deviceServiceApi: "https://b1prod-api.fortiq.titans.cloud/ris-public-api",
  },
  prod: {
    risPublicApi: "https://api.honeywellhome.com/ris-public-api",
    deviceServiceApi: "https://api.honeywellhome.com/ris-public-api",
  },
};

/**
 * Get current environment from env var or default to QA
 */
function getCurrentEnvironment(): Environment {
  const env = import.meta.env.VITE_API_ENVIRONMENT as Environment;
  return env && API_URLS[env] ? env : "qa";
}

/**
 * Get API URLs for the current environment, allowing VITE_RIS_BASE_URL to
 * override the default for the resolved environment (e.g. FA59.stage uses
 * stage auth but a different API host).
 */
function getApiUrls(env: Environment): ApiConfig {
  const defaults = API_URLS[env];
  const risOverride = import.meta.env.VITE_RIS_BASE_URL as string | undefined;
  return {
    risPublicApi: risOverride || defaults.risPublicApi,
    deviceServiceApi: risOverride || defaults.deviceServiceApi,
  };
}

/**
 * Build a fetch `init` object that has the Authorization header injected.
 */
function buildAuthHeaders(
  init: RequestInit | undefined,
  token: string | null
): RequestInit {
  const enhancedInit = { ...(init ?? {}) };
  const headers = new Headers(enhancedInit.headers);

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (!headers.has("Content-Type") && enhancedInit.body) {
    headers.set("Content-Type", "application/json");
  }

  enhancedInit.headers = headers;
  return enhancedInit;
}

/**
 * openapi-fetch dispatches `fetch(new Request(...))`. Passing a second `init`
 * with `headers` alongside a Request *replaces* that Request's headers, which
 * drops its `Content-Type` and makes the API answer 415 — so merge instead.
 */
function withAuthHeaders(request: Request, token: string | null): Request {
  if (!token) {
    return request;
  }

  const headers = new Headers(request.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return new Request(request, { headers });
}

/**
 * Custom fetch wrapper that:
 *  1. Attaches the Auth0 Bearer token to every request.
 *  2. On 401, force-refreshes the token via the Auth0 refresh token and
 *     retries the request exactly once before returning the 401 response.
 *
 * This covers the openapi-fetch clients (risPublicApiClient,
 * deviceServiceApiClient). The apiService HTTP methods have an equivalent
 * retry via `fetchWithRetry`.
 */
const createAuthenticatedFetch = (): typeof fetch => {
  return async (url, init) => {
    // Resolve token — Auth0 SDK serves from cache or refreshes silently
    let token = await apiService.getAuthToken();
    if (!token) {
      token = localStorage.getItem("authToken");
    }

    const isRequest = typeof Request !== "undefined" && url instanceof Request;
    // A Request body can only be read once, so keep a copy for the 401 retry.
    const retrySource = isRequest ? (url as Request).clone() : url;

    const send = (source: RequestInfo | URL, authToken: string | null) =>
      typeof Request !== "undefined" && source instanceof Request
        ? fetch(withAuthHeaders(source, authToken))
        : fetch(source, buildAuthHeaders(init, authToken));

    // First attempt
    let response = await send(url, token);

    // On 401: force-refresh (bypass cache, use refresh token) and retry once
    if (response.status === 401) {
      const freshToken = await apiService.forceRefreshToken();
      if (freshToken) {
        response = await send(retrySource, freshToken);
      }
      // If still 401 after retry, return it — the caller / openapi-fetch
      // will surface it as an error without logging the user out here.
    }

    return response;
  };
};

/**
 * Current environment
 */
const currentEnv = getCurrentEnvironment();
const apiUrls = getApiUrls(currentEnv);

/**
 * RIS Public API Client
 *
 * Main API for residential integration services.
 *
 * Usage:
 * ```typescript
 * import { risPublicApiClient } from './api/apiConfig';
 *
 * const { data, error } = await risPublicApiClient.GET('/endpoint');
 * ```
 *
 * @generated - DO NOT EDIT MANUALLY
 * Regenerate with: npm run generate:apis
 */
export const risPublicApiClient = createClient<RisPublicApiPaths>({
  baseUrl: apiUrls.risPublicApi,
  fetch: createAuthenticatedFetch(),
});

/**
 * Device Service Public API Client
 *
 * Device management and operations API.
 *
 * Usage:
 * ```typescript
 * import { deviceServiceApiClient } from './api/apiConfig';
 *
 * const { data, error } = await deviceServiceApiClient.GET('/devices');
 * ```
 *
 * @generated - DO NOT EDIT MANUALLY
 * Regenerate with: npm run generate:apis
 */
export const deviceServiceApiClient = createClient<DeviceServiceApiPaths>({
  baseUrl: apiUrls.deviceServiceApi,
  fetch: createAuthenticatedFetch(),
});

/**
 * Get current environment
 */
export const getEnvironment = (): Environment => currentEnv;

/**
 * Get API base URL for specific service
 */
export const getApiUrl = (service: keyof ApiConfig): string => {
  return apiUrls[service];
};

/**
 * Switch environment (for testing purposes)
 * Note: In production, use VITE_API_ENVIRONMENT env var
 */
export const setEnvironment = (_env: Environment): void => {
  // Environment switching at runtime is not recommended in production.
  // Set VITE_API_ENVIRONMENT and reload the app instead.
  // This would require recreating clients - better to set env var and reload
};
